import {
  ActionError,
  Button,
  EmptyState,
  FilterSegments,
  ListRow,
  ListRows,
  PageHeader,
  PageTitle,
  RowActions,
  SearchInput,
  Select,
  StatusTag,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  plural,
  titleCase,
  toast,
  useBreakpoint,
  useDelayed,
  type FilterSegmentOption,
} from '@westhetics/ui'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { PeriodFilter } from '@/app/period-filter'
import { Page } from '@/app/shell'
import { usePractitionerOptions } from '@/features/users/api'
import { errorMessage } from '@/lib/errors'
import { useSession } from '@/lib/session'
import { routes } from '@/routes'
import {
  useExportPatients,
  usePatientAction,
  usePatients,
  useProcedureFilterOptions,
  type Audience,
  type HighlightField,
  type PatientFilter,
  type PatientListItem,
  type PatientListMeta,
  type PatientListParams,
  type PatientPerPage,
  type PatientSort,
} from './api'
import { saveFile } from './download'
import { todayIso } from './form'
import { Highlighted } from './highlight'
import {
  AUDIENCE_LABELS,
  displayWhatsapp,
  formatDocument,
  personWords,
  type PersonWords,
} from './labels'
import {
  readStoredList,
  usePatientListState,
  useSavedSearches,
  normalizeSearch,
} from './list-state'
import { MobileActionBar } from './mobile-action'

/**
 * Pacientes (docs/areas/pacientes.md, "Lista"; RN-PAC-10 a 16 e 22). Arquétipo: lista de
 * trabalho. A pergunta: "quais pacientes precisam de algo de mim?". Abre em "Com pendência"
 * quando há pendência, ordena por urgência e guarda segmento, busca, ordem e página na URL, para
 * voltar igual depois de uma ficha. Abaixo de 900px a tabela vira cartão e a ação principal fica
 * fixa ao alcance do polegar.
 */

const PER_PAGES: PatientPerPage[] = [10, 25, 50]

export function PatientsPage() {
  const session = useSession()
  const clinicId = session.profile?.clinicId ?? ''
  const [params] = useSearchParams()
  const isBareUrl = params.toString() === ''
  // Quem chega sem nada na URL volta para a última lista que viu nesta clínica (RN-PAC-15).
  const [stored] = useState(() => (isBareUrl ? readStoredList(clinicId) : ''))
  const [isRestored, setIsRestored] = useState(stored === '')
  if (!isRestored && !isBareUrl) setIsRestored(true)
  if (!isRestored) {
    return <Navigate to={{ pathname: routes.patients, search: `?${stored}` }} replace />
  }
  return <PatientList clinicId={clinicId} />
}

function PatientList({ clinicId }: { clinicId: string }) {
  const session = useSession()
  const navigate = useNavigate()
  const isDesk = useBreakpoint('desk')
  const isWide = useBreakpoint('wide')
  const canManage = session.canManageClinic
  const { state, update } = usePatientListState(clinicId)
  const savedSearches = useSavedSearches(session.profile?.userId ?? '', clinicId)

  // A busca espera 300 ms sem tecla antes de ir para a URL (e dali para a API).
  const [text, setText] = useState(state.search)
  const latestUpdate = useRef(update)
  const timer = useRef<number | undefined>(undefined)
  useEffect(() => {
    latestUpdate.current = update
  }, [update])
  useEffect(() => () => window.clearTimeout(timer.current), [])

  function typeSearch(value: string) {
    setText(value)
    window.clearTimeout(timer.current)
    timer.current = window.setTimeout(
      () => latestUpdate.current({ search: normalizeSearch(value) }),
      300,
    )
  }

  /** Busca aplicada na hora: limpar, ou escolher uma busca salva. */
  function applySearch(value: string) {
    window.clearTimeout(timer.current)
    setText(value)
    update({ search: value })
  }

  // Arquivados é de gestora e root (RN-PAC-18): para os demais, o endereço vale como sem escolha.
  const chosen = state.filter === 'archived' && !canManage ? null : state.filter
  // A tela abre no que tem pendência; sem pendência, em Todos (RN-PAC-12). Decidido uma vez, na
  // primeira resposta, e mantido até a pessoa escolher um segmento.
  const [opened, setOpened] = useState<'pending' | 'all' | null>(null)
  const filter: PatientFilter = chosen ?? opened ?? 'pending'
  const hasExtraFilters = Boolean(
    state.practitionerId || state.procedureId || state.from || state.to,
  )
  // O público da pessoa (RN-PAC-39): a lista abre em Pacientes; o segmento só aparece com mais de um.
  const audience: Audience = state.audience || 'patient'
  const words = personWords(audience)
  const params: PatientListParams = {
    audience,
    filter,
    search: state.search || undefined,
    sort: state.sort,
    page: state.page,
    perPage: state.perPage,
    practitionerId: state.practitionerId || undefined,
    procedureId: state.procedureId || undefined,
    from: state.from || undefined,
    to: state.to || undefined,
  }
  const query = usePatients(params)
  const counts = query.data?.meta.counts
  if (chosen === null && opened === null && counts && !query.isPlaceholderData) {
    setOpened(counts.pending > 0 ? 'pending' : 'all')
  }

  const practitioners = usePractitionerOptions(isDesk)
  const procedures = useProcedureFilterOptions(isDesk)
  const action = usePatientAction()
  const exporter = useExportPatients()
  const canExport = canManage
  const isExportSlow = useDelayed(exporter.isPending)
  const isSearchSlow = useDelayed(query.isFetching && Boolean(state.search))

  const patients = query.data?.data ?? []
  const meta = query.data?.meta
  const audiences = meta?.audiences ?? []
  const audienceOptions: FilterSegmentOption<Audience>[] = audiences.map((item) => ({
    value: item.audience,
    label: AUDIENCE_LABELS[item.audience],
    count: item.count,
  }))
  // A resposta anterior segura a tela enquanto a nova chega; vazia, ela não diz nada.
  const isLoading = query.isPending || (query.isPlaceholderData && patients.length === 0)
  const isEmptyClinic =
    !isLoading &&
    counts?.all === 0 &&
    !state.search &&
    text.trim() === '' &&
    !hasExtraFilters &&
    filter !== 'archived'
  const isSaved = savedSearches.saved.includes(state.search)
  // O total da clínica, visto pela última vez sem busca nem filtro: "Buscando entre 331 pacientes."
  const [clinicTotal, setClinicTotal] = useState<number | null>(null)
  if (
    counts &&
    !query.isPlaceholderData &&
    !state.search &&
    !hasExtraFilters &&
    counts.all !== clinicTotal
  ) {
    setClinicTotal(counts.all)
  }

  const options: FilterSegmentOption<PatientFilter>[] = [
    { value: 'pending', label: 'Com pendência', count: counts?.pending },
    // No celular ficam os dois segmentos do dia a dia; os outros só aparecem se já escolhidos.
    ...(isDesk || filter === 'incomplete'
      ? [{ value: 'incomplete' as const, label: 'Cadastro incompleto', count: counts?.incomplete }]
      : []),
    { value: 'all', label: 'Todos', count: counts?.all },
    ...(canManage && (isDesk || filter === 'archived')
      ? [
          {
            value: 'archived' as const,
            label: 'Arquivados',
            // A API não conta os arquivados: o número só existe com a lista deles na tela.
            count: filter === 'archived' && !query.isPlaceholderData ? meta?.total : undefined,
          },
        ]
      : []),
  ]

  function restore(patient: PatientListItem) {
    action.mutate(
      { id: patient.id, action: 'restore' },
      {
        onSuccess: () => toast(`${titleCase(patient.name)} restaurada.`),
        onError: (error) => toast(errorMessage(error)),
      },
    )
  }

  function exportCsv() {
    const total = meta?.total ?? 0
    exporter.mutate(params, {
      onSuccess: (file) => {
        saveFile(file, `${fileStem(words.many)}-${todayIso()}.csv`)
        toast(
          `${plural(total, words.one, words.many)} ${total === 1 ? 'exportado' : 'exportados'}.`,
        )
      },
    })
  }

  function clearExtraFilters() {
    update({ practitionerId: '', procedureId: '', from: '', to: '' })
  }

  const createLink = (
    <Link
      to={audience === 'patient' ? routes.newPatient : `${routes.newPatient}?audience=${audience}`}
    >
      + Criar {words.one}
    </Link>
  )

  return (
    <>
      <PageHeader
        crumbs={[{ label: 'Operação' }, { label: 'Pacientes' }]}
        actions={
          isDesk ? (
            <>
              <Button variant="outline" size="sm" asChild>
                <Link to={routes.patientRegistrationLink}>Link de cadastro</Link>
              </Button>
              {canExport ? (
                <Button
                  variant="outline"
                  size="sm"
                  loading={exporter.isPending}
                  loadingLabel="Exportando…"
                  onClick={exportCsv}
                >
                  Exportar CSV
                </Button>
              ) : null}
              <Button size="sm" asChild>
                {createLink}
              </Button>
            </>
          ) : null
        }
      />
      <Page wide>
        <PageTitle title={words.titleMany} subtitle={subtitle(filter, state.search, meta, words)} />

        {audienceOptions.length > 1 ? (
          <div className="mt-5">
            <FilterSegments
              label="Filtrar por público"
              options={audienceOptions}
              value={audience}
              onChange={(value) => update({ audience: value === 'patient' ? '' : value })}
            />
          </div>
        ) : null}

        {query.isError ? (
          <LoadError
            className="mt-6"
            title={`Não conseguimos carregar a lista de ${words.many}`}
            error={query.error}
            onRetry={() => void query.refetch()}
          />
        ) : isEmptyClinic ? (
          <EmptyState
            className="mt-6"
            title={`Nenhum ${words.one} ainda`}
            description={
              audience === 'patient'
                ? 'Cadastre o primeiro: nome e WhatsApp bastam para começar. O resto entra pela ficha ou pela anamnese que a paciente responde.'
                : 'Cadastre o primeiro: nome e WhatsApp bastam para começar. O resto entra pela ficha, de onde saem os documentos avulsos.'
            }
            action={
              <>
                <Button asChild>{createLink}</Button>
                {canManage ? (
                  <Button variant="outline" onClick={() => update({ filter: 'archived' })}>
                    Ver arquivados
                  </Button>
                ) : null}
              </>
            }
          />
        ) : (
          <>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <SearchInput
                value={text}
                onChange={typeSearch}
                onClear={() => applySearch('')}
                placeholder={`Buscar nome, ${words.document} ou telefone`}
                aria-label={`Buscar ${words.one}`}
              />
              <FilterSegments
                label={`Filtrar ${words.many}`}
                options={options}
                value={filter}
                onChange={(value) => update({ filter: value })}
              />
            </div>

            {isDesk ? (
              <div className="mt-3 flex flex-wrap items-start gap-3">
                {practitioners.data && practitioners.data.length > 0 ? (
                  <div className="w-[220px]">
                    <Select
                      aria-label="Filtrar por prestador"
                      placeholder="Todos os prestadores"
                      value={state.practitionerId}
                      onChange={(event) => update({ practitionerId: event.target.value })}
                    >
                      {practitioners.data.map((item) => (
                        <option key={item.id} value={item.id}>
                          {titleCase(item.name)}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : null}
                {procedures.data && procedures.data.length > 0 ? (
                  <div className="w-[220px]">
                    <Select
                      aria-label="Filtrar por procedimento"
                      placeholder="Todos os procedimentos"
                      value={state.procedureId}
                      onChange={(event) => update({ procedureId: event.target.value })}
                    >
                      {procedures.data.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : null}
                <PeriodFilter
                  fromLabel="Atendimentos a partir de"
                  toLabel="Atendimentos até"
                  key={`${state.from}|${state.to}`}
                  from={state.from}
                  to={state.to}
                  onApply={(from, to) => update({ from, to })}
                />
                {hasExtraFilters ? (
                  <Button variant="text" className="mt-3" onClick={clearExtraFilters}>
                    Limpar filtros
                  </Button>
                ) : null}
              </div>
            ) : null}

            {state.search && meta && meta.total > 0 ? (
              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1">
                <span className="font-body text-secondary text-[12px] leading-[1.6] font-light">
                  {plural(meta.total, 'resultado')} para "{state.search}"
                </span>
                {isSaved ? (
                  <Button
                    variant="text"
                    className="text-[12px]"
                    onClick={() => savedSearches.remove(state.search)}
                  >
                    remover dos salvos
                  </Button>
                ) : (
                  <Button
                    variant="text"
                    className="text-[12px]"
                    onClick={() => {
                      savedSearches.save(state.search)
                      toast('Busca salva neste navegador.')
                    }}
                  >
                    salvar esta busca
                  </Button>
                )}
              </div>
            ) : !state.search && text === '' && savedSearches.saved.length > 0 ? (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="font-body text-secondary text-[12px] leading-[1.6] font-light">
                  Buscas salvas
                </span>
                {savedSearches.saved.map((search) => (
                  <Button
                    key={search}
                    variant="outline"
                    size="sm"
                    onClick={() => applySearch(search)}
                  >
                    {search}
                  </Button>
                ))}
              </div>
            ) : null}

            {exporter.isError ? (
              <ActionError
                className="mt-4"
                title="Não foi possível exportar"
                description={`${errorMessage(exporter.error)} A lista continua na tela.`}
                onDismiss={() => exporter.reset()}
              />
            ) : null}
            {isExportSlow ? <Waiting>Preparando o arquivo para você levar.</Waiting> : null}

            <div className="mt-4">
              {isLoading ? (
                <>
                  <TableSkeleton rows={6} columns={isDesk ? 4 : 2} />
                  <LoadingPhrase words={words} />
                </>
              ) : patients.length === 0 ? (
                <EmptyList
                  words={words}
                  elsewhere={audiences.filter(
                    (item) => item.audience !== audience && item.count > 0,
                  )}
                  filter={filter}
                  search={state.search}
                  allCount={counts?.all ?? 0}
                  hasExtraFilters={hasExtraFilters}
                  onClearSearch={() => applySearch('')}
                  onShowAll={() => update({ filter: 'all' })}
                  onClearFilters={clearExtraFilters}
                />
              ) : (
                <>
                  {isSearchSlow ? (
                    <Waiting>
                      {clinicTotal === null
                        ? `Buscando entre os seus ${words.many}.`
                        : `Buscando entre ${plural(clinicTotal, words.one, words.many)}.`}
                    </Waiting>
                  ) : null}
                  {isDesk ? (
                    <Table>
                      <TableHeader>
                        <tr>
                          <TableHead
                            sorted={
                              state.sort === 'name' ? 'asc' : state.sort === '-name' ? 'desc' : null
                            }
                            onSort={() => update({ sort: nextNameSort(state.sort) })}
                          >
                            Nome
                          </TableHead>
                          <TableHead>E-mail</TableHead>
                          <TableHead>Telefone</TableHead>
                          {isWide ? <TableHead>{words.document}</TableHead> : null}
                          {isWide ? <TableHead>RG</TableHead> : null}
                          <TableHead
                            sorted={state.sort === 'urgency' ? 'desc' : null}
                            onSort={() => update({ sort: 'urgency' })}
                          >
                            Pendência
                          </TableHead>
                          <TableHead aria-label="Ações" />
                        </tr>
                      </TableHeader>
                      <TableBody>
                        {patients.map((patient) => (
                          <PatientRow
                            key={patient.id}
                            patient={patient}
                            isWide={isWide}
                            canManage={canManage}
                            isArchivedList={filter === 'archived'}
                            onOpen={() => navigate(routes.patient(patient.id))}
                            onRestore={() => restore(patient)}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <ListRows>
                      {patients.map((patient) => (
                        <PatientCard
                          key={patient.id}
                          patient={patient}
                          onOpen={() => navigate(routes.patient(patient.id))}
                        />
                      ))}
                    </ListRows>
                  )}
                </>
              )}
              {meta && patients.length > 0 && !isLoading ? (
                <ListFooter
                  words={words}
                  meta={meta}
                  sort={state.sort}
                  onPage={(page) => update({ page })}
                  onPerPage={(perPage) => update({ perPage })}
                />
              ) : null}
            </div>
          </>
        )}
      </Page>

      {isDesk ? null : (
        <MobileActionBar>
          <Button size="lg" asChild>
            {createLink}
          </Button>
        </MobileActionBar>
      )}
    </>
  )
}

/** O total e o critério: "6 pacientes com algo pendente de 331 no total". */
function subtitle(
  filter: PatientFilter,
  search: string,
  meta: PatientListMeta | undefined,
  words: PersonWords,
) {
  if (!meta) return 'Quem a clínica atende, com o que falta de cada um.'
  const { pending, all } = meta.counts
  if (filter === 'archived') {
    return `${plural(meta.total, 'arquivado')} · fora das listas e da busca, com anamneses, documentos e planos guardados`
  }
  if (filter === 'pending') {
    return `${plural(pending, words.one, words.many)} com algo pendente de ${all} no total`
  }
  const registered = search
    ? plural(all, 'encontrado')
    : all === 1
      ? '1 cadastrado'
      : `${all} cadastrados`
  return pending > 0 ? `${registered} · ${pending} com pendência` : registered
}

/** "funcionários" vira "funcionarios": o nome do arquivo vai sem acento. */
function fileStem(text: string) {
  return text.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
}

/** Nome ordena ida e volta; qualquer outra ordem começa pelo A. */
function nextNameSort(sort: PatientSort): PatientSort {
  return sort === 'name' ? '-name' : 'name'
}

function snippetOf(patient: PatientListItem, field: HighlightField) {
  return patient.highlights.find((highlight) => highlight.field === field)?.snippet
}

const supportClass = 'font-body mt-1 text-[12px] leading-[1.5] font-light'

function PatientRow({
  patient,
  isWide,
  canManage,
  isArchivedList,
  onOpen,
  onRestore,
}: {
  patient: PatientListItem
  isWide: boolean
  canManage: boolean
  isArchivedList: boolean
  onOpen: () => void
  onRestore: () => void
}) {
  const { pendingItem } = patient
  const docNumber = patient.docNumber ? formatDocument(patient.docNumber, patient.docType) : ''
  const isCnpj = patient.docType === 'cnpj'
  const docSnippet = snippetOf(patient, 'docNumber')
  const rgSnippet = snippetOf(patient, 'rg')
  const foreignSnippet = snippetOf(patient, 'foreignDocument')
  const isFromNetwork = patient.sourceClinic !== null
  return (
    <TableRow critical={pendingItem.level === 'critical'} onClick={onOpen}>
      <TableCell primary>
        <div className="min-w-0">
          <Highlighted text={titleCase(patient.name)} snippet={snippetOf(patient, 'name')} />
          {/* O que a busca achou num campo que não está à vista aparece sob o nome. */}
          {!isWide && docSnippet && docNumber ? (
            <div className={`${supportClass} text-secondary`}>
              {isCnpj ? 'CNPJ' : 'CPF'}{' '}
              <Highlighted text={docNumber} snippet={docSnippet} digitsOnly={!isCnpj} />
            </div>
          ) : null}
          {!isWide && rgSnippet && patient.rg ? (
            <div className={`${supportClass} text-secondary`}>
              RG <Highlighted text={patient.rg} snippet={rgSnippet} />
            </div>
          ) : null}
          {foreignSnippet ? (
            <div className={`${supportClass} text-secondary`}>
              Documento estrangeiro <Highlighted text={foreignSnippet} snippet={foreignSnippet} />
            </div>
          ) : null}
          {pendingItem.total > 1 ? (
            <div
              className={`${supportClass} ${pendingItem.level === 'waiting' ? 'text-secondary' : 'text-plum'}`}
            >
              {plural(pendingItem.total, 'pendência')}
            </div>
          ) : null}
          {patient.duplicates.total > 1 ? (
            <div className={`${supportClass} text-secondary`}>
              {patient.duplicates.total} cadastros duplicados —{' '}
              {canManage && !isFromNetwork ? (
                <Link
                  to={routes.patientDuplicates(patient.id)}
                  className="text-ink hover:border-ink border-hairline-heavy border-b"
                  onClick={(event) => event.stopPropagation()}
                >
                  unificar?
                </Link>
              ) : (
                'avise a gestora'
              )}
            </div>
          ) : null}
          {patient.sourceClinic ? (
            <div className={`${supportClass} text-secondary`}>
              Cadastro de {patient.sourceClinic.name}
            </div>
          ) : null}
        </div>
      </TableCell>
      <TableCell muted className="break-all">
        {patient.email ? (
          <Highlighted text={patient.email} snippet={snippetOf(patient, 'email')} />
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell muted className="whitespace-nowrap">
        {patient.whatsapp ? (
          <Highlighted
            text={displayWhatsapp(patient.whatsapp)}
            snippet={snippetOf(patient, 'whatsapp')}
            digitsOnly
          />
        ) : (
          '—'
        )}
      </TableCell>
      {isWide ? (
        <TableCell muted className="whitespace-nowrap">
          {docNumber ? (
            <Highlighted text={docNumber} snippet={docSnippet} digitsOnly={!isCnpj} />
          ) : (
            '—'
          )}
        </TableCell>
      ) : null}
      {isWide ? (
        <TableCell muted>
          {patient.rg ? <Highlighted text={patient.rg} snippet={rgSnippet} /> : '—'}
        </TableCell>
      ) : null}
      <TableCell>
        {pendingItem.level && pendingItem.label ? (
          <StatusTag level={pendingItem.level}>{pendingItem.label}</StatusTag>
        ) : (
          <span className="text-label">—</span>
        )}
      </TableCell>
      <TableCell>
        <RowActions>
          <Link to={routes.patient(patient.id)}>abrir ficha</Link>
          {isArchivedList ? (
            canManage ? (
              <button type="button" onClick={onRestore}>
                restaurar
              </button>
            ) : null
          ) : isFromNetwork ? null : (
            <Link to={routes.patientTab(patient.id, 'details')}>editar</Link>
          )}
        </RowActions>
      </TableCell>
    </TableRow>
  )
}

/** O cartão do celular: nome, "67 99686-0918 · 1 pendência" e a etiqueta; a linha inteira toca. */
function PatientCard({ patient, onOpen }: { patient: PatientListItem; onOpen: () => void }) {
  const { pendingItem } = patient
  const context = [
    displayWhatsapp(patient.whatsapp),
    pendingItem.total > 0 ? plural(pendingItem.total, 'pendência') : '',
  ].filter(Boolean)
  return (
    <ListRow
      size="name"
      critical={pendingItem.level === 'critical'}
      onClick={onOpen}
      title={<Highlighted text={titleCase(patient.name)} snippet={snippetOf(patient, 'name')} />}
      meta={
        <>
          {context.join(' · ')}
          {pendingItem.level && pendingItem.label ? (
            <span className="mt-2 block">
              <StatusTag level={pendingItem.level}>{pendingItem.label}</StatusTag>
            </span>
          ) : null}
        </>
      }
    />
  )
}

/** O vazio de cada recorte: o que é, por que está vazio e a saída. */
function EmptyList({
  words,
  elsewhere,
  filter,
  search,
  allCount,
  hasExtraFilters,
  onClearSearch,
  onShowAll,
  onClearFilters,
}: {
  words: PersonWords
  /** Os outros públicos com cadastro, para dizer onde a busca pode estar (RN-PAC-39). */
  elsewhere: { audience: Audience; count: number }[]
  filter: PatientFilter
  search: string
  allCount: number
  hasExtraFilters: boolean
  onClearSearch: () => void
  onShowAll: () => void
  onClearFilters: () => void
}) {
  const showAll = (
    <Button variant="outline" onClick={onShowAll}>
      Ver todos
    </Button>
  )
  if (search) {
    // O termo existe fora do segmento: a saída é trocar de segmento, não desistir da busca.
    const isElsewhere = filter !== 'all' && filter !== 'archived' && allCount > 0
    const otherAudiences = elsewhere
      .map((item) => {
        const other = personWords(item.audience)
        return `${plural(item.count, other.one, other.many)} em ${other.titleMany}`
      })
      .join(' e ')
    return (
      <EmptyState
        title={
          isElsewhere
            ? `Nenhum ${words.one} com esse termo neste filtro`
            : `Nenhum ${words.one} com esse termo`
        }
        description={
          isElsewhere
            ? `A busca encontrou ${plural(allCount, words.one, words.many)} em Todos. Este filtro mostra só uma parte deles.`
            : otherAudiences
              ? `A busca encontrou ${otherAudiences}. Escolha o público acima para ver.`
              : `A busca cobre nome, ${words.document} e telefone. Verifique a grafia ou crie o ${words.one} agora.`
        }
        action={
          <>
            {isElsewhere ? showAll : null}
            <Button variant="outline" onClick={onClearSearch}>
              Limpar busca
            </Button>
          </>
        }
      />
    )
  }
  if (hasExtraFilters) {
    return (
      <EmptyState
        title={`Nenhum ${words.one} nestes filtros`}
        description="Prestador, procedimento e período valem por atendimento: só aparece quem tem um atendimento com essa combinação."
        action={
          <Button variant="outline" onClick={onClearFilters}>
            Limpar filtros
          </Button>
        }
      />
    )
  }
  if (filter === 'archived') {
    return (
      <EmptyState
        title={`Nenhum ${words.one} arquivado`}
        description="O cadastro que você arquivar sai das listas e da busca, aparece aqui e volta a qualquer momento, com anamneses, documentos e planos guardados."
        action={showAll}
      />
    )
  }
  if (filter === 'incomplete') {
    return (
      <EmptyState
        title="Nenhum cadastro incompleto"
        description={`Todo ${words.one} tem os dados que o contrato pede: documento, nascimento, endereço, estado civil e e-mail.`}
        action={showAll}
      />
    )
  }
  if (filter === 'pending') {
    return (
      <EmptyState
        title={`Nenhum ${words.one} com pendência`}
        description="Nada depende da clínica agora: não há documento sem assinatura, anamnese sem resposta nem plano sem registro."
        action={showAll}
      />
    )
  }
  return (
    <EmptyState
      title={`Nenhum ${words.one} nesta página`}
      description="A lista mudou enquanto você navegava. Volte ao início para ver quem está cadastrado."
      action={showAll}
    />
  )
}

/** A frase de espera só aparece se a carga passar de 800 ms (docs/styleguide/04-escrita.md). */
function LoadingPhrase({ words }: { words: PersonWords }) {
  const isSlow = useDelayed(true)
  return isSlow ? <Waiting>Reunindo seus {words.many}.</Waiting> : null
}

function Waiting({ children }: { children: ReactNode }) {
  return (
    <p
      role="status"
      aria-live="polite"
      className="font-body text-name text-secondary my-3 leading-[1.6] font-light"
    >
      {children}
    </p>
  )
}

function ListFooter({
  words,
  meta,
  sort,
  onPage,
  onPerPage,
}: {
  words: PersonWords
  meta: PatientListMeta
  sort: PatientSort
  onPage: (page: number) => void
  onPerPage: (perPage: PatientPerPage) => void
}) {
  const first = (meta.page - 1) * meta.perPage + 1
  const last = Math.min(meta.page * meta.perPage, meta.total)
  const count =
    meta.totalPages > 1
      ? `${first}–${last} de ${meta.total}`
      : plural(meta.total, words.one, words.many)
  const order =
    sort === 'urgency'
      ? 'por urgência'
      : sort === 'name' || sort === '-name'
        ? 'em ordem alfabética'
        : 'por data de cadastro'
  const linkClass =
    'hover:text-ink disabled:text-label-soft max-desk:min-h-touch cursor-pointer disabled:cursor-default'
  return (
    <TableFooter>
      <span>
        {count} · {order} · a busca cobre nome, {words.document} e telefone
      </span>
      <span className="flex flex-wrap items-center gap-4">
        {meta.total > PER_PAGES[0]! ? (
          <span
            role="group"
            aria-label={`${words.titleMany} por página`}
            className="flex items-center gap-3"
          >
            por página
            {PER_PAGES.map((size) => (
              <button
                key={size}
                type="button"
                aria-pressed={meta.perPage === size}
                onClick={() => onPerPage(size)}
                className={`${linkClass} ${meta.perPage === size ? 'text-ink border-ink border-b' : ''}`}
              >
                {size}
              </button>
            ))}
          </span>
        ) : null}
        {meta.totalPages > 1 ? (
          <>
            <button
              type="button"
              disabled={meta.page <= 1}
              onClick={() => onPage(meta.page - 1)}
              className={linkClass}
            >
              anterior
            </button>
            <button
              type="button"
              disabled={meta.page >= meta.totalPages}
              onClick={() => onPage(meta.page + 1)}
              className={linkClass}
            >
              próxima
            </button>
          </>
        ) : null}
      </span>
    </TableFooter>
  )
}
